import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Archive, ArchiveRestore, Trash2, Loader2,
  Database, AlertTriangle, RefreshCw, CheckSquare, Square,
} from "lucide-react";
import { apiClient, formatApiError } from "@/lib/auth";

/**
 * Super-admin Database Manager · /admin/db
 * Per-row Archive (soft delete) + Hard Delete + Bulk actions across
 * every operational collection. Auth/config collections are excluded
 * from the whitelist on the backend so you can't lock yourself out.
 */
export default function DatabaseManagerPage() {
  const [collections, setCollections] = useState([]);
  const [active, setActive] = useState("friction_audits");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null);   // {rowId, primary} or {bulk: true, count}

  const loadCollections = async () => {
    try {
      const { data } = await apiClient.get("/admin/db/collections");
      setCollections(data.collections || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Failed to load collections");
    } finally { setLoading(false); }
  };

  const loadRows = async () => {
    if (!active) return;
    setRowsLoading(true);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (includeArchived) params.set("include_archived", "true");
      if (q.trim())        params.set("q", q.trim());
      const { data } = await apiClient.get(`/admin/db/collections/${active}?${params}`);
      setRows(data.rows || []);
      setSelected(new Set());
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRowsLoading(false); }
  };

  useEffect(() => { loadCollections(); }, []);
  useEffect(() => { loadRows(); /* eslint-disable-next-line */ }, [active, includeArchived]);

  const activeMeta = useMemo(() => collections.find((c) => c.collection === active), [collections, active]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === rows.length && rows.length > 0) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  const archiveRow = async (row) => {
    try {
      await apiClient.post(`/admin/db/collections/${active}/${row.id}/archive`);
      toast.success(`Archived · ${row.primary}`);
      await Promise.all([loadRows(), loadCollections()]);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const restoreRow = async (row) => {
    try {
      await apiClient.post(`/admin/db/collections/${active}/${row.id}/restore`);
      toast.success(`Restored · ${row.primary}`);
      await Promise.all([loadRows(), loadCollections()]);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const hardDeleteRow = async (row) => {
    try {
      await apiClient.delete(`/admin/db/collections/${active}/${row.id}`);
      toast.success(`Hard-deleted · ${row.primary}`);
      setConfirmDelete(null);
      await Promise.all([loadRows(), loadCollections()]);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const bulkArchive = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const { data } = await apiClient.post(`/admin/db/collections/${active}/bulk-archive`, { ids });
      toast.success(`Archived ${data.archived} row${data.archived === 1 ? "" : "s"}`);
      await Promise.all([loadRows(), loadCollections()]);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const { data } = await apiClient.post(`/admin/db/collections/${active}/bulk-delete`, { ids });
      toast.success(`Hard-deleted ${data.deleted} row${data.deleted === 1 ? "" : "s"}`);
      setConfirmDelete(null);
      await Promise.all([loadRows(), loadCollections()]);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] p-6 md:p-10">
      <div className="max-w-6xl mx-auto">
        <Link to="/admin" className="mono-body text-[11px] text-nex-chrome hover:text-nex-gold inline-flex items-center gap-2 mb-6">
          <ArrowLeft size={14} /> Back to Console
        </Link>

        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="mono-label mb-2">// DATABASE MANAGER</div>
            <h1 className="serif-head text-3xl md:text-4xl flex items-center gap-3">
              <Database size={30} className="text-nex-gold" /> Data Hygiene
            </h1>
            <p className="text-nex-chrome mt-2 text-sm max-w-2xl">
              Archive or hard-delete rows across every operational collection. Auth users,
              schema, labels, and brand config are protected — you can&apos;t break the console from here.
            </p>
          </div>
          <button onClick={() => Promise.all([loadCollections(), loadRows()])} className="tick-btn-secondary text-xs !py-2 !px-3" data-testid="db-refresh">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16"><Loader2 className="animate-spin text-nex-gold mx-auto" /></div>
        ) : (
          <>
            {/* Collection selector */}
            <div className="flex flex-wrap gap-2 mb-4" data-testid="db-collection-tabs">
              {collections.map((c) => (
                <button
                  key={c.collection}
                  data-testid={`db-tab-${c.collection}`}
                  onClick={() => setActive(c.collection)}
                  className={
                    active === c.collection
                      ? "text-xs px-3 py-1.5 border border-nex-gold/60 bg-nex-gold/10 text-nex-gold font-mono uppercase tracking-[0.14em]"
                      : "text-xs px-3 py-1.5 border border-white/10 bg-white/[0.02] text-nex-chrome hover:text-nex-silver font-mono uppercase tracking-[0.14em] transition"
                  }
                >
                  {c.label} <span className="opacity-70">({c.active}{c.archived ? ` · ${c.archived} arch` : ""})</span>
                </button>
              ))}
            </div>

            {/* Search + include-archived + bulk actions */}
            <div className="card-industrial corner-tick p-3 flex flex-col md:flex-row gap-3 md:items-center mb-4" data-testid="db-toolbar">
              <form onSubmit={(e) => { e.preventDefault(); loadRows(); }} className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nex-muted" />
                <input
                  data-testid="db-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onBlur={loadRows}
                  className="input-industrial pl-9 !py-2"
                  placeholder={`Filter ${activeMeta?.label || "rows"} — name, email, note, id…`}
                />
              </form>
              <label className="flex items-center gap-2 text-xs text-nex-chrome cursor-pointer whitespace-nowrap">
                <input
                  data-testid="db-include-archived"
                  type="checkbox"
                  checked={includeArchived}
                  onChange={(e) => setIncludeArchived(e.target.checked)}
                  className="accent-nex-gold"
                />
                Include archived
              </label>
              {selected.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="mono-label text-nex-gold whitespace-nowrap">{selected.size} selected</span>
                  <button onClick={bulkArchive} data-testid="db-bulk-archive" className="tick-btn-secondary text-xs !py-2 !px-3">
                    <Archive size={13} /> Archive
                  </button>
                  <button
                    onClick={() => setConfirmDelete({ bulk: true, count: selected.size })}
                    data-testid="db-bulk-delete"
                    className="text-xs !py-2 !px-3 border border-red-500/50 bg-red-500/10 text-red-300 hover:bg-red-500/20 font-mono uppercase tracking-[0.14em] inline-flex items-center gap-1.5"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              )}
            </div>

            {/* Rows table */}
            <div className="card-industrial corner-tick overflow-hidden" data-testid="db-rows-card">
              {rowsLoading ? (
                <div className="text-center py-16"><Loader2 className="animate-spin text-nex-gold mx-auto" /></div>
              ) : rows.length === 0 ? (
                <div className="text-center py-16 text-nex-muted mono-body text-sm">No rows.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-white/10">
                      <th className="pb-3 pl-4 pr-2 w-8">
                        <button onClick={toggleAll} data-testid="db-select-all" className="text-nex-muted hover:text-nex-gold">
                          {selected.size === rows.length && rows.length > 0
                            ? <CheckSquare size={16} className="text-nex-gold" />
                            : <Square size={16} />}
                        </button>
                      </th>
                      <th className="mono-label pb-3 pr-4">Row</th>
                      <th className="mono-label pb-3 pr-4">Detail</th>
                      <th className="mono-label pb-3 pr-4">Tag</th>
                      <th className="mono-label pb-3 pr-4">When</th>
                      <th className="mono-label pb-3 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody data-testid="db-rows-body">
                    {rows.map((row) => {
                      const on = selected.has(row.id);
                      return (
                        <tr key={row.id} className={`border-b border-white/5 hover:bg-white/[0.02] ${row.archived ? "opacity-60" : ""}`} data-testid={`db-row-${row.id}`}>
                          <td className="py-3 pl-4 pr-2 align-top">
                            <button onClick={() => toggle(row.id)} className="text-nex-muted hover:text-nex-gold" data-testid={`db-row-select-${row.id}`}>
                              {on ? <CheckSquare size={16} className="text-nex-gold" /> : <Square size={16} />}
                            </button>
                          </td>
                          <td className="py-3 pr-4 align-top">
                            <div className="text-nex-silver font-medium">{row.primary || "(empty)"}</div>
                            <div className="text-[10px] text-nex-muted mono-body">{row.id.slice(0, 8)}…</div>
                          </td>
                          <td className="py-3 pr-4 align-top text-nex-chrome text-xs">{row.secondary || "—"}</td>
                          <td className="py-3 pr-4 align-top mono-body text-[11px] text-nex-muted">{row.tag || "—"}</td>
                          <td className="py-3 pr-4 align-top mono-body text-[10px] text-nex-muted whitespace-nowrap">
                            {row.created_at ? row.created_at.slice(0, 10) : "—"}
                            {row.archived && <div className="text-red-400/80">archived</div>}
                          </td>
                          <td className="py-3 pr-4 align-top">
                            <div className="flex items-center gap-1 justify-end">
                              {row.archived ? (
                                <button onClick={() => restoreRow(row)} data-testid={`db-restore-${row.id}`} className="w-7 h-7 grid place-items-center border border-white/10 text-nex-chrome hover:text-nex-gold hover:border-nex-gold/50 transition" title="Restore">
                                  <ArchiveRestore size={13} />
                                </button>
                              ) : (
                                <button onClick={() => archiveRow(row)} data-testid={`db-archive-${row.id}`} className="w-7 h-7 grid place-items-center border border-white/10 text-nex-chrome hover:text-nex-gold hover:border-nex-gold/50 transition" title="Archive (soft delete)">
                                  <Archive size={13} />
                                </button>
                              )}
                              <button
                                onClick={() => setConfirmDelete({ rowId: row.id, primary: row.primary })}
                                data-testid={`db-delete-${row.id}`}
                                className="w-7 h-7 grid place-items-center border border-red-500/30 text-red-300 hover:bg-red-500/10 hover:border-red-500/60 transition"
                                title="Hard delete (permanent)"
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>

      {/* Confirm hard-delete dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="db-confirm-dialog" onClick={() => setConfirmDelete(null)}>
          <div className="card-industrial corner-tick max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 grid place-items-center border border-red-500/50 bg-red-500/10 text-red-300">
                <AlertTriangle size={20} />
              </div>
              <div>
                <div className="mono-label mb-1 text-red-300">// HARD DELETE · PERMANENT</div>
                <h3 className="serif-head text-lg text-nex-silver">
                  {confirmDelete.bulk
                    ? `Delete ${confirmDelete.count} row${confirmDelete.count === 1 ? "" : "s"} permanently?`
                    : `Delete "${confirmDelete.primary}" permanently?`}
                </h3>
                <p className="text-xs text-nex-chrome mt-1">
                  This can&apos;t be undone. Prefer <span className="text-nex-gold">Archive</span> if you might need this back.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDelete(null)} className="tick-btn-secondary text-xs !py-2 !px-3">Cancel</button>
              <button
                onClick={() => confirmDelete.bulk ? bulkDelete() : hardDeleteRow({ id: confirmDelete.rowId, primary: confirmDelete.primary })}
                data-testid="db-confirm-delete"
                className="text-xs !py-2 !px-3 border border-red-500 bg-red-500/20 text-red-200 hover:bg-red-500/30 font-mono uppercase tracking-[0.14em] inline-flex items-center gap-1.5"
              >
                <Trash2 size={13} /> Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
